import type { AdherenceIndexSummary, DiagnosticSnapshot, SectionEnvelope } from "@/api/diagnostic";

import { rec } from "../diagnosticData";

type Validation = DiagnosticSnapshot["validation"];

/**
 * O que o tile e o frame "Aderência ao padrão" sabem dizer (EF-FUNC-011, RF-06 a
 * RF-09).
 *
 * - `counted`: o índice calculado pelo backend, inteiro. A tela não reconta:
 *   percentuais, cobertura e "Preliminar" vêm prontos, e o PDF e o Resumo leem o
 *   mesmo resumo.
 * - `empty`: nenhum scope item, WRICEF ou GAP contado ou pendente. Não é 0%.
 * - `failed`: o domínio não fechou, ou fechou `ok` sem o resumo (contrato
 *   quebrado). O índice pode existir, então não se manda esperar outra análise.
 * - `unknown`: o bloco não chegou.
 */
export type AdherenceIndexState =
  | { kind: "counted"; index: AdherenceIndexSummary }
  | { kind: "empty" }
  | { kind: "failed" }
  | { kind: "unknown" };

/**
 * Lê o envelope `adherence_index` do bloco `validation`, e não uma seção: o
 * índice sai de decisão humana e é calculado a cada leitura, também com a
 * Calibragem publicada.
 */
export function adherenceIndex(validation: Validation | undefined): AdherenceIndexState {
  const envelope = validation?.adherence_index;
  if (!envelope || envelope.state === "not_collected") return { kind: "unknown" };
  if (envelope.state === "empty") return { kind: "empty" };
  if (envelope.state !== "ok") return { kind: "failed" };

  const data = rec(envelope.data);
  const rows = rec(data.rows);
  if (!rows.consolidated || !data.composition || typeof data.preliminary !== "boolean") {
    return { kind: "failed" };
  }
  return { kind: "counted", index: data as unknown as AdherenceIndexSummary };
}

/**
 * O envelope para a casca do frame. Sem ele (backend anterior ao índice), o
 * frame fica "não coletado" com a mesma origem do índice, e não some.
 */
export function adherenceEnvelope(validation: Validation | undefined): SectionEnvelope {
  const envelope = validation?.adherence_index;
  if (envelope) return envelope;
  return {
    code: "KEY_ABSENT",
    state: "not_collected",
    reason: "",
    source: { kind: "derived", ref: "derived:adherence_index", snapshot: "derived" },
    data: {},
  } as SectionEnvelope;
}
